import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { getVariant, getVariantList } from '@/services/variant.service';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { QueryKey } from '@/lib/queryKeys';

export const useVariant = (id: string, options?: object) => {
  const variantQueryFn = useAuthenticatedQueryFn(getVariant);

  const {
    data: variant = [],
    isPending: variantIsPending,
    isFetching: variantIsFetching,
    error: variantError,
    refetch: refetchVariant,
  } = useQuery({
    queryKey: [QueryKey.Variant, id],
    queryFn: () => variantQueryFn(id),
    ...options,
  });

  return {
    variant,
    variantIsFetching,
    variantIsPending,
    variantError,
    refetchVariant,
  };
};

export const useVariantList = (
  params: {
    pagination: { limit: number | null; offset: number | null };
    sort: { order: string; value: string };
    queryString: string;
  },
  options?: object,
) => {
  const variantListQueryFn = useAuthenticatedQueryFn(getVariantList);

  const {
    data: variantList = {
      variants: [],
      pagination: { count: 0, limit: 0, offset: 0 },
      usageCounters: { image: 0, video: 0 },
      sort: { order: SortOrderOptions.ASC, value: SortOrderValues.CREATED_AT },
    },
    isPending,
    isFetching,
    isFetched,
    error,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.VariantList, params.pagination, params.queryString, params.sort],
    queryFn: () => variantListQueryFn(params),
    placeholderData: keepPreviousData,
    ...options,
  });

  return {
    data: variantList.variants,
    usageCounters: variantList.usageCounters,
    pagination: variantList.pagination,
    isFetching,
    isPending,
    isFetched,
    error,
    refetch,
  };
};
